import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  Calendar,
  Clock,
  MapPin,
  Users,
  FileText,
  LogOut,
  Building2,
  Search,
  ChevronRight,
  Copy,
  Trash2,
  LayoutTemplate,
  UserPlus,
  X,
  ListOrdered,
  Bell,
  AlertTriangle,
  Settings,
  CreditCard,
  Sparkles,
  TrendingUp,
  Palette,
  Archive,
  RotateCcw,
  Shield,
  CheckCircle2,
  AlertCircle,
  Activity,
  Lock,
  LayoutDashboard,
  FolderOpen,
  ChevronDown,
  Menu,
  Zap,
  BarChart3,
  ClipboardList,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [archivedMeetings, setArchivedMeetings] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showFromTemplateModal, setShowFromTemplateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState("meetings");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [archivingMeeting, setArchivingMeeting] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  const [newMeeting, setNewMeeting] = useState({
    name: "",
    committee_name: "",
    date: "",
    start_time: "",
    end_time: "",
    venue: "",
  });

  const [newTemplate, setNewTemplate] = useState({
    name: "",
    committee_name: "",
    default_venue: "",
    default_start_time: "09:00",
    default_end_time: "11:00",
    default_attendees: [],
    agenda_items: [],
  });

  const [newAttendee, setNewAttendee] = useState({
    person_name: "",
    role_title: "",
    attendance_type: "present",
  });

  const [newAgendaItem, setNewAgendaItem] = useState({
    title: "",
    description: "",
  });

  const [fromTemplateData, setFromTemplateData] = useState({
    template_id: "",
    date: "",
    start_time: "",
    end_time: "",
    venue: "",
  });

  const [reminders, setReminders] = useState(null);

  useEffect(() => {
    fetchData();
    fetchReminders();
    fetchSubscription();
  }, []);

  const fetchData = async () => {
    try {
      const [meetingsRes, archivedRes, templatesRes] = await Promise.all([
        axios.get(`${API}/meetings`),
        axios.get(`${API}/meetings?archived=true`),
        axios.get(`${API}/templates`),
      ]);
      setMeetings(meetingsRes.data);
      setArchivedMeetings(archivedRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveMeeting = async (meetingId) => {
    if (!window.confirm("Archive this meeting? It will be moved to the Archive tab.")) {
      return;
    }
    
    setArchivingMeeting(meetingId);
    try {
      await axios.post(`${API}/meetings/${meetingId}/archive`);
      toast.success("Meeting archived");
      fetchData();
    } catch (error) {
      toast.error("Failed to archive meeting");
    } finally {
      setArchivingMeeting(null);
    }
  };

  const handleUnarchiveMeeting = async (meetingId) => {
    setArchivingMeeting(meetingId);
    try {
      await axios.post(`${API}/meetings/${meetingId}/unarchive`);
      toast.success("Meeting restored");
      fetchData();
    } catch (error) {
      toast.error("Failed to restore meeting");
    } finally {
      setArchivingMeeting(null);
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm("Are you sure you want to permanently delete this meeting? This cannot be undone.")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/meetings/${meetingId}`);
      toast.success("Meeting deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete meeting");
    }
  };

  const fetchReminders = async () => {
    try {
      const response = await axios.get(`${API}/reminders/summary`);
      setReminders(response.data);
    } catch (error) {
      console.error("Failed to fetch reminders");
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`);
      setSubscription(response.data);
    } catch (error) {
      console.error("Failed to fetch subscription");
    }
  };

  const handleCreateMeeting = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const response = await axios.post(`${API}/meetings`, newMeeting);
      setMeetings([response.data, ...meetings]);
      setShowCreateModal(false);
      setNewMeeting({
        name: "",
        committee_name: "",
        date: "",
        start_time: "",
        end_time: "",
        venue: "",
      });
      toast.success("Meeting created successfully");
      navigate(`/meetings/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create meeting");
    } finally {
      setCreating(false);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const response = await axios.post(`${API}/templates`, newTemplate);
      setTemplates([response.data, ...templates]);
      setShowTemplateModal(false);
      setNewTemplate({
        name: "",
        committee_name: "",
        default_venue: "",
        default_start_time: "09:00",
        default_end_time: "11:00",
        default_attendees: [],
        agenda_items: [],
      });
      toast.success("Template created successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create template");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("Are you sure you want to delete this template?")) return;
    
    try {
      await axios.delete(`${API}/templates/${templateId}`);
      setTemplates(templates.filter((t) => t.id !== templateId));
      toast.success("Template deleted");
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const handleCreateFromTemplate = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const response = await axios.post(`${API}/meetings/from-template`, fromTemplateData);
      setMeetings([response.data, ...meetings]);
      setShowFromTemplateModal(false);
      setFromTemplateData({
        template_id: "",
        date: "",
        start_time: "",
        end_time: "",
        venue: "",
      });
      setSelectedTemplate(null);
      toast.success("Meeting created from template");
      navigate(`/meetings/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create meeting");
    } finally {
      setCreating(false);
    }
  };

  const addAttendeeToTemplate = () => {
    if (!newAttendee.person_name || !newAttendee.role_title) return;
    setNewTemplate({
      ...newTemplate,
      default_attendees: [...newTemplate.default_attendees, { ...newAttendee }],
    });
    setNewAttendee({ person_name: "", role_title: "", attendance_type: "present" });
  };

  const removeAttendeeFromTemplate = (index) => {
    setNewTemplate({
      ...newTemplate,
      default_attendees: newTemplate.default_attendees.filter((_, i) => i !== index),
    });
  };

  const addAgendaItemToTemplate = () => {
    if (!newAgendaItem.title) return;
    setNewTemplate({
      ...newTemplate,
      agenda_items: [...newTemplate.agenda_items, { ...newAgendaItem }],
    });
    setNewAgendaItem({ title: "", description: "" });
  };

  const removeAgendaItemFromTemplate = (index) => {
    setNewTemplate({
      ...newTemplate,
      agenda_items: newTemplate.agenda_items.filter((_, i) => i !== index),
    });
  };

  const openFromTemplate = (template) => {
    setSelectedTemplate(template);
    setFromTemplateData({
      template_id: template.id,
      date: "",
      start_time: template.default_start_time,
      end_time: template.default_end_time,
      venue: template.default_venue,
    });
    setShowFromTemplateModal(true);
  };

  const filteredMeetings = meetings.filter(
    (m) =>
      m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.committee_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTemplates = templates.filter(
    (t) =>
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.committee_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (meeting) => {
    if (meeting.has_minutes) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
          <CheckCircle2 className="w-3 h-3" />
          Minutes Ready
        </span>
      );
    }
    if (meeting.has_transcript) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-700 border border-amber-200">
          <ClipboardList className="w-3 h-3" />
          Transcribed
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-slate-100 text-slate-600 border border-slate-200">
        <FileText className="w-3 h-3" />
        Draft
      </span>
    );
  };

  // Quorum Quick Check badge - shows at-a-glance quorum status
  const getQuorumBadge = (meeting) => {
    const presentCount = meeting.attendees?.filter(a => a.attendance_type === "present")?.length || 0;
    const quorumRule = meeting.quorum_rule;
    
    // If quorum is already confirmed, show green
    if (meeting.quorum_confirmed) {
      return (
        <span 
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200"
          title="Quorum confirmed"
          data-testid={`quorum-badge-${meeting.id}`}
        >
          <CheckCircle2 className="w-3 h-3" />
          Quorum ✓
        </span>
      );
    }
    
    // Check against quorum rule if set
    if (quorumRule?.min_total_attendees) {
      const minRequired = quorumRule.min_total_attendees;
      if (presentCount >= minRequired) {
        return (
          <span 
            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200"
            title={`${presentCount}/${minRequired} required attendees`}
            data-testid={`quorum-badge-${meeting.id}`}
          >
            <CheckCircle2 className="w-3 h-3" />
            {presentCount}/{minRequired}
          </span>
        );
      } else {
        return (
          <span 
            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-red-50 text-red-600 border border-red-200"
            title={`Need ${minRequired - presentCount} more attendees`}
            data-testid={`quorum-badge-${meeting.id}`}
          >
            <AlertCircle className="w-3 h-3" />
            {presentCount}/{minRequired}
          </span>
        );
      }
    }
    
    // Default: show attendee count without quorum rule
    if (presentCount > 0) {
      return (
        <span 
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-slate-50 text-slate-500 border border-slate-200"
          title="No quorum rule set"
          data-testid={`quorum-badge-${meeting.id}`}
        >
          <Users className="w-3 h-3" />
          {presentCount}
        </span>
      );
    }
    
    return null;
  };

  // Sidebar menu items
  const menuItems = [
    { id: "meetings", label: "Meetings", icon: Calendar, count: meetings.length },
    { id: "archive", label: "Archive", icon: Archive, count: archivedMeetings.length },
    { id: "templates", label: "Templates", icon: LayoutTemplate, count: templates.length },
  ];

  const quickActions = [
    { label: "Calendar", icon: Calendar, onClick: () => navigate("/calendar"), color: "bg-blue-500" },
    { label: "Action Register", icon: ClipboardList, onClick: () => navigate("/action-register"), color: "bg-amber-500" },
    { label: "Branding", icon: Palette, onClick: () => navigate("/branding"), color: "bg-purple-500" },
    { label: "Billing", icon: CreditCard, onClick: () => navigate("/billing"), color: "bg-green-500" },
  ];

  const adminActions = [
    { label: "Compliance", icon: Shield, onClick: () => navigate("/compliance") },
    { label: "Audit Log", icon: Activity, onClick: () => navigate("/audit-log") },
    { label: "Legal Holds", icon: Lock, onClick: () => navigate("/legal-holds") },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
      {/* Modern Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Brand */}
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="lg:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <Menu className="w-5 h-5 text-slate-600" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-700 rounded-xl flex items-center justify-center shadow-lg shadow-teal-500/20">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="font-bold text-lg text-slate-800 tracking-tight">MyMeetingsPal</h1>
                  <p className="text-xs text-slate-500 -mt-0.5">Corporate Minutes Generator</p>
                </div>
              </div>
            </div>

            {/* Center - Search */}
            <div className="hidden md:flex flex-1 max-w-xl mx-8">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search meetings, templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-50 border-slate-200 focus:bg-white transition-colors w-full"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              {/* Subscription Badge */}
              {subscription && (
                <button
                  onClick={() => navigate("/billing")}
                  className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all hover:scale-105 ${
                    subscription.subscription?.status === "trialing"
                      ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25"
                      : subscription.subscription?.status === "active"
                      ? "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/25"
                      : subscription.subscription?.status === "past_due"
                      ? "bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/25"
                      : "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg shadow-red-500/25"
                  }`}
                  data-testid="subscription-badge"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  {subscription.subscription?.plan_name || "Starter"}
                  {subscription.subscription?.status === "trialing" && " Trial"}
                </button>
              )}

              {/* Organization */}
              <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-lg">
                <Building2 className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">{user?.entity_name}</span>
              </div>

              {/* User Menu */}
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center text-white font-semibold shadow-lg shadow-teal-500/20">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="hidden sm:block">
                  <p className="text-sm font-semibold text-slate-800">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.role?.replace('_', ' ')}</p>
                </div>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-slate-500 hover:text-red-600 hover:bg-red-50"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Subscription Upgrade Banner */}
      {subscription?.limits?.upgrade_required && (
        <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white">
          <div className="max-w-[1600px] mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <p className="text-sm font-medium">
                  {subscription.limits.reason}
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => navigate("/pricing")}
                className="bg-white text-orange-600 hover:bg-orange-50 font-semibold shadow-lg"
                data-testid="upgrade-btn"
              >
                <TrendingUp className="w-4 h-4 mr-1" />
                Upgrade Plan
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className={`${sidebarCollapsed ? 'hidden' : 'block'} lg:block w-72 flex-shrink-0`}>
            <div className="sticky top-24 space-y-6">
              {/* Quick Stats Cards */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">{meetings.length}</p>
                      <p className="text-xs text-slate-500">Active</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                      <CheckCircle2 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">{meetings.filter(m => m.has_minutes).length}</p>
                      <p className="text-xs text-slate-500">Complete</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation Menu */}
              <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Navigation</h3>
                </div>
                <div className="p-2">
                  {menuItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                        activeTab === item.id
                          ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-lg shadow-teal-500/25'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                      data-testid={`${item.id}-tab`}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                      </div>
                      {item.count > 0 && (
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                          activeTab === item.id 
                            ? 'bg-white/20 text-white' 
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {item.count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Quick Actions</h3>
                </div>
                <div className="p-3 grid grid-cols-2 gap-2">
                  {quickActions.map((action) => (
                    <button
                      key={action.label}
                      onClick={action.onClick}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl hover:bg-slate-50 transition-colors group"
                    >
                      <div className={`w-10 h-10 ${action.color} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                        <action.icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-xs font-medium text-slate-600">{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Admin Actions */}
              {(user?.role === "company_admin" || user?.role === "admin") && (
                <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
                  <div className="p-4 border-b border-slate-100">
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Administration</h3>
                  </div>
                  <div className="p-2">
                    {adminActions.map((action) => (
                      <button
                        key={action.label}
                        onClick={action.onClick}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors"
                      >
                        <action.icon className="w-5 h-5" />
                        <span className="font-medium">{action.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Reminders Banner */}
            {reminders && (reminders.overdue_count > 0 || reminders.due_today_count > 0) && (
              <div className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/60 rounded-2xl p-5 shadow-sm" data-testid="reminders-banner">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20 flex-shrink-0">
                    <Bell className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-amber-900 mb-2">Action Item Reminders</h3>
                    <div className="flex flex-wrap gap-4 text-sm">
                      {reminders.overdue_count > 0 && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 rounded-full font-medium">
                          <AlertTriangle className="w-4 h-4" />
                          {reminders.overdue_count} overdue
                        </div>
                      )}
                      {reminders.due_today_count > 0 && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full font-medium">
                          <Clock className="w-4 h-4" />
                          {reminders.due_today_count} due today
                        </div>
                      )}
                      {reminders.due_this_week_count > 0 && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                          <Calendar className="w-4 h-4" />
                          {reminders.due_this_week_count} this week
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">
                  {activeTab === "meetings" && "Meetings"}
                  {activeTab === "archive" && "Archived Meetings"}
                  {activeTab === "templates" && "Templates"}
                </h2>
                <p className="text-slate-500 mt-1">
                  {activeTab === "meetings" && "Manage your corporate meetings and minutes"}
                  {activeTab === "archive" && "View and restore archived meetings"}
                  {activeTab === "templates" && "Create reusable meeting templates"}
                </p>
              </div>

              <div className="flex items-center gap-3">
                {activeTab === "meetings" && (
                  <>
                    {templates.length > 0 && (
                      <Select
                        onValueChange={(templateId) => {
                          const template = templates.find((t) => t.id === templateId);
                          if (template) openFromTemplate(template);
                        }}
                      >
                        <SelectTrigger className="w-[180px] bg-white" data-testid="use-template-select">
                          <Copy className="w-4 h-4 mr-2 text-slate-400" />
                          <SelectValue placeholder="Use Template" />
                        </SelectTrigger>
                        <SelectContent>
                          {templates.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                      <DialogTrigger asChild>
                        <Button
                          className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white shadow-lg shadow-teal-500/25 font-semibold"
                          data-testid="create-meeting-btn"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          New Meeting
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-lg">
                        <DialogHeader>
                          <DialogTitle className="text-xl font-bold">
                            Create New Meeting
                          </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleCreateMeeting} className="space-y-4 mt-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                              <Label className="text-slate-700 font-medium">Meeting Name</Label>
                              <Input
                                value={newMeeting.name}
                                onChange={(e) =>
                                  setNewMeeting({ ...newMeeting, name: e.target.value })
                                }
                                placeholder="Q4 Board Meeting"
                                required
                                className="mt-1.5"
                                data-testid="meeting-name-input"
                              />
                            </div>

                            <div className="col-span-2">
                              <Label className="text-slate-700 font-medium">Committee/Board Name</Label>
                              <Input
                                value={newMeeting.committee_name}
                                onChange={(e) =>
                                  setNewMeeting({
                                    ...newMeeting,
                                    committee_name: e.target.value,
                                  })
                                }
                                placeholder="Board of Directors"
                                required
                                className="mt-1.5"
                                data-testid="committee-name-input"
                              />
                            </div>

                            <div>
                              <Label className="text-slate-700 font-medium">Date</Label>
                              <Input
                                type="date"
                                value={newMeeting.date}
                                onChange={(e) =>
                                  setNewMeeting({ ...newMeeting, date: e.target.value })
                                }
                                required
                                className="mt-1.5"
                                data-testid="meeting-date-input"
                              />
                            </div>

                            <div>
                              <Label className="text-slate-700 font-medium">Venue/Platform</Label>
                              <Input
                                value={newMeeting.venue}
                                onChange={(e) =>
                                  setNewMeeting({ ...newMeeting, venue: e.target.value })
                                }
                                placeholder="Conference Room A"
                                required
                                className="mt-1.5"
                                data-testid="meeting-venue-input"
                              />
                            </div>

                            <div>
                              <Label className="text-slate-700 font-medium">Start Time</Label>
                              <Input
                                type="time"
                                value={newMeeting.start_time}
                                onChange={(e) =>
                                  setNewMeeting({
                                    ...newMeeting,
                                    start_time: e.target.value,
                                  })
                                }
                                required
                                className="mt-1.5"
                                data-testid="meeting-start-time-input"
                              />
                            </div>

                            <div>
                              <Label className="text-slate-700 font-medium">End Time</Label>
                              <Input
                                type="time"
                                value={newMeeting.end_time}
                                onChange={(e) =>
                                  setNewMeeting({
                                    ...newMeeting,
                                    end_time: e.target.value,
                                  })
                                }
                                required
                                className="mt-1.5"
                                data-testid="meeting-end-time-input"
                              />
                            </div>
                          </div>

                          <div className="flex justify-end gap-3 pt-4">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setShowCreateModal(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700"
                              disabled={creating}
                              data-testid="submit-meeting-btn"
                            >
                              {creating ? "Creating..." : "Create Meeting"}
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </>
                )}

                {activeTab === "templates" && (
                  <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
                    <DialogTrigger asChild>
                      <Button
                        className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white shadow-lg shadow-teal-500/25 font-semibold"
                        data-testid="create-template-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        New Template
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-xl font-bold">
                          Create Meeting Template
                        </DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateTemplate} className="space-y-4 mt-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-2">
                            <Label className="text-slate-700 font-medium">Template Name</Label>
                            <Input
                              value={newTemplate.name}
                              onChange={(e) =>
                                setNewTemplate({ ...newTemplate, name: e.target.value })
                              }
                              placeholder="Monthly Board Meeting"
                              required
                              className="mt-1.5"
                              data-testid="template-name-input"
                            />
                          </div>

                          <div className="col-span-2">
                            <Label className="text-slate-700 font-medium">Committee/Board Name</Label>
                            <Input
                              value={newTemplate.committee_name}
                              onChange={(e) =>
                                setNewTemplate({
                                  ...newTemplate,
                                  committee_name: e.target.value,
                                })
                              }
                              placeholder="Board of Directors"
                              required
                              className="mt-1.5"
                              data-testid="template-committee-input"
                            />
                          </div>

                          <div className="col-span-2">
                            <Label className="text-slate-700 font-medium">Default Venue</Label>
                            <Input
                              value={newTemplate.default_venue}
                              onChange={(e) =>
                                setNewTemplate({
                                  ...newTemplate,
                                  default_venue: e.target.value,
                                })
                              }
                              placeholder="Boardroom - Level 10"
                              className="mt-1.5"
                              data-testid="template-venue-input"
                            />
                          </div>

                          <div>
                            <Label className="text-slate-700 font-medium">Default Start Time</Label>
                            <Input
                              type="time"
                              value={newTemplate.default_start_time}
                              onChange={(e) =>
                                setNewTemplate({
                                  ...newTemplate,
                                  default_start_time: e.target.value,
                                })
                              }
                              className="mt-1.5"
                              data-testid="template-start-time-input"
                            />
                          </div>

                          <div>
                            <Label className="text-slate-700 font-medium">Default End Time</Label>
                            <Input
                              type="time"
                              value={newTemplate.default_end_time}
                              onChange={(e) =>
                                setNewTemplate({
                                  ...newTemplate,
                                  default_end_time: e.target.value,
                                })
                              }
                              className="mt-1.5"
                              data-testid="template-end-time-input"
                            />
                          </div>
                        </div>

                        {/* Default Attendees */}
                        <div className="border-t border-slate-200 pt-4">
                          <Label className="text-base font-semibold text-slate-800">Default Attendees</Label>
                          <p className="text-sm text-slate-500 mb-3">
                            These attendees will be added automatically when creating a meeting
                          </p>

                          {newTemplate.default_attendees.length > 0 && (
                            <div className="space-y-2 mb-3">
                              {newTemplate.default_attendees.map((att, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                                >
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                                      <span className="text-xs font-medium text-slate-600">
                                        {att.person_name.charAt(0).toUpperCase()}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-sm font-medium text-slate-800">{att.person_name}</span>
                                      <span className="text-xs text-slate-500 ml-2">{att.role_title}</span>
                                    </div>
                                    <span className={`text-xs ml-2 px-2 py-0.5 rounded-full font-medium ${
                                      att.attendance_type === "present"
                                        ? "bg-emerald-100 text-emerald-700"
                                        : att.attendance_type === "apology"
                                        ? "bg-amber-100 text-amber-700"
                                        : "bg-blue-100 text-blue-700"
                                    }`}>
                                      {att.attendance_type}
                                    </span>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeAttendeeFromTemplate(idx)}
                                    className="text-slate-400 hover:text-red-500"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="grid grid-cols-4 gap-2">
                            <Input
                              placeholder="Name"
                              value={newAttendee.person_name}
                              onChange={(e) =>
                                setNewAttendee({ ...newAttendee, person_name: e.target.value })
                              }
                              data-testid="template-attendee-name"
                            />
                            <Input
                              placeholder="Role"
                              value={newAttendee.role_title}
                              onChange={(e) =>
                                setNewAttendee({ ...newAttendee, role_title: e.target.value })
                              }
                              data-testid="template-attendee-role"
                            />
                            <Select
                              value={newAttendee.attendance_type}
                              onValueChange={(v) =>
                                setNewAttendee({ ...newAttendee, attendance_type: v })
                              }
                            >
                              <SelectTrigger data-testid="template-attendee-type">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="present">Present</SelectItem>
                                <SelectItem value="apology">Apology</SelectItem>
                                <SelectItem value="invitee">Invitee</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={addAttendeeToTemplate}
                              data-testid="add-template-attendee-btn"
                            >
                              <UserPlus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Agenda Items */}
                        <div className="border-t border-slate-200 pt-4">
                          <Label className="text-base font-semibold text-slate-800">Agenda Items</Label>
                          <p className="text-sm text-slate-500 mb-3">
                            Define standard agenda topics for this meeting type
                          </p>

                          {newTemplate.agenda_items.length > 0 && (
                            <div className="space-y-2 mb-3">
                              {newTemplate.agenda_items.map((item, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-start justify-between p-3 bg-slate-50 rounded-xl"
                                >
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="w-6 h-6 flex items-center justify-center text-xs font-bold bg-teal-100 text-teal-700 rounded-full">
                                        {idx + 1}
                                      </span>
                                      <span className="text-sm font-medium text-slate-800">{item.title}</span>
                                    </div>
                                    {item.description && (
                                      <p className="text-xs text-slate-500 mt-1 ml-8">
                                        {item.description}
                                      </p>
                                    )}
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeAgendaItemFromTemplate(idx)}
                                    className="text-slate-400 hover:text-red-500"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="space-y-2">
                            <Input
                              placeholder="Agenda item title (e.g., Approval of Previous Minutes)"
                              value={newAgendaItem.title}
                              onChange={(e) =>
                                setNewAgendaItem({ ...newAgendaItem, title: e.target.value })
                              }
                              data-testid="template-agenda-title"
                            />
                            <div className="flex gap-2">
                              <Input
                                placeholder="Description (optional)"
                                value={newAgendaItem.description}
                                onChange={(e) =>
                                  setNewAgendaItem({ ...newAgendaItem, description: e.target.value })
                                }
                                className="flex-1"
                                data-testid="template-agenda-description"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                onClick={addAgendaItemToTemplate}
                                data-testid="add-template-agenda-btn"
                              >
                                <ListOrdered className="w-4 h-4 mr-1" />
                                Add
                              </Button>
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-4">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowTemplateModal(false)}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700"
                            disabled={creating}
                            data-testid="submit-template-btn"
                          >
                            {creating ? "Creating..." : "Create Template"}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </div>

            {/* Content Tabs */}
            {/* Meetings Content */}
            {activeTab === "meetings" && (
              <>
                {loading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : filteredMeetings.length === 0 ? (
                  <div className="text-center py-20 bg-white border border-slate-200/60 rounded-2xl shadow-sm">
                    <div className="w-20 h-20 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <Calendar className="w-10 h-10 text-slate-400" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">
                      No meetings yet
                    </h3>
                    <p className="text-slate-500 mb-6 max-w-sm mx-auto">
                      Create your first meeting to start generating professional corporate minutes
                    </p>
                    <Button
                      onClick={() => setShowCreateModal(true)}
                      className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 shadow-lg shadow-teal-500/25"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Meeting
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="meetings-list">
                    {filteredMeetings.map((meeting) => (
                      <div
                        key={meeting.id}
                        onClick={() => navigate(`/meetings/${meeting.id}`)}
                        className="bg-white border border-slate-200/60 rounded-2xl p-6 hover:border-teal-300 hover:shadow-lg hover:shadow-teal-500/5 transition-all cursor-pointer group"
                        data-testid={`meeting-card-${meeting.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <h3 className="text-lg font-bold text-slate-800 group-hover:text-teal-700 transition-colors">
                                {meeting.name}
                              </h3>
                              {getStatusBadge(meeting)}
                              {getQuorumBadge(meeting)}
                            </div>

                            <p className="text-slate-600 font-medium mb-4">
                              {meeting.committee_name}
                            </p>

                            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                              <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg">
                                <Calendar className="w-4 h-4 text-slate-400" />
                                <span>{meeting.date}</span>
                              </div>
                              <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg">
                                <Clock className="w-4 h-4 text-slate-400" />
                                <span>{meeting.start_time} - {meeting.end_time}</span>
                              </div>
                              <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg">
                                <MapPin className="w-4 h-4 text-slate-400" />
                                <span>{meeting.venue}</span>
                              </div>
                              {meeting.attendees?.length > 0 && (
                                <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg">
                                  <Users className="w-4 h-4 text-slate-400" />
                                  <span>{meeting.attendees.length} attendees</span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {meeting.has_minutes && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleArchiveMeeting(meeting.id);
                                }}
                                disabled={archivingMeeting === meeting.id}
                                title="Archive meeting"
                                className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-teal-600"
                                data-testid={`archive-meeting-${meeting.id}`}
                              >
                                {archivingMeeting === meeting.id ? (
                                  <span className="w-4 h-4 animate-spin border-2 border-slate-300 border-t-teal-600 rounded-full" />
                                ) : (
                                  <Archive className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            <div className="w-10 h-10 bg-slate-100 group-hover:bg-teal-100 rounded-xl flex items-center justify-center transition-colors">
                              <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-teal-600 transition-colors" />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Archive Content */}
            {activeTab === "archive" && (
              <>
                {loading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : archivedMeetings.length === 0 ? (
                  <div className="text-center py-20 bg-white border border-slate-200/60 rounded-2xl shadow-sm">
                    <div className="w-20 h-20 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <Archive className="w-10 h-10 text-slate-400" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">
                      No archived meetings
                    </h3>
                    <p className="text-slate-500 max-w-sm mx-auto">
                      Archived meetings will appear here after you finalize them
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="archived-meetings-list">
                    {archivedMeetings.map((meeting) => (
                      <div
                        key={meeting.id}
                        className="bg-white border border-slate-200/60 rounded-2xl p-5 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1 cursor-pointer" onClick={() => navigate(`/meetings/${meeting.id}`)}>
                            <h3 className="font-bold text-slate-800 mb-1">
                              {meeting.name}
                            </h3>
                            <p className="text-sm text-slate-500">{meeting.committee_name}</p>
                          </div>
                          <span className="px-2.5 py-1 text-xs font-semibold bg-slate-100 text-slate-600 rounded-full">
                            Archived
                          </span>
                        </div>

                        <div className="space-y-2 text-sm text-slate-600 mb-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            {meeting.date}
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-slate-400" />
                            {meeting.venue || "No venue set"}
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                          <div className="flex items-center gap-2">
                            {meeting.has_minutes && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded-full font-medium">
                                <FileText className="w-3 h-3" />
                                Minutes
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUnarchiveMeeting(meeting.id)}
                              disabled={archivingMeeting === meeting.id}
                              title="Restore meeting"
                              className="text-slate-500 hover:text-teal-600"
                              data-testid={`unarchive-meeting-${meeting.id}`}
                            >
                              {archivingMeeting === meeting.id ? (
                                <span className="w-4 h-4 animate-spin border-2 border-slate-300 border-t-teal-600 rounded-full" />
                              ) : (
                                <RotateCcw className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteMeeting(meeting.id)}
                              className="text-slate-500 hover:text-red-600 hover:bg-red-50"
                              data-testid={`delete-archived-meeting-${meeting.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Templates Content */}
            {activeTab === "templates" && (
              <>
                {loading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : filteredTemplates.length === 0 ? (
                  <div className="text-center py-20 bg-white border border-slate-200/60 rounded-2xl shadow-sm">
                    <div className="w-20 h-20 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <LayoutTemplate className="w-10 h-10 text-slate-400" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">
                      No templates yet
                    </h3>
                    <p className="text-slate-500 mb-6 max-w-sm mx-auto">
                      Create templates for recurring meetings with predefined attendees and agendas
                    </p>
                    <Button
                      onClick={() => setShowTemplateModal(true)}
                      className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 shadow-lg shadow-teal-500/25"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Template
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="templates-list">
                    {filteredTemplates.map((template) => (
                      <div
                        key={template.id}
                        className="bg-white border border-slate-200/60 rounded-2xl p-6 hover:border-teal-300 hover:shadow-lg transition-all"
                        data-testid={`template-card-${template.id}`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/20">
                            <LayoutTemplate className="w-6 h-6 text-white" />
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="text-slate-400 hover:text-red-600"
                            data-testid={`delete-template-${template.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>

                        <h3 className="text-lg font-bold text-slate-800 mb-1">
                          {template.name}
                        </h3>
                        <p className="text-sm text-slate-600 mb-4">
                          {template.committee_name}
                        </p>

                        <div className="space-y-2 text-sm text-slate-500 mb-6">
                          {template.default_venue && (
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-slate-400" />
                              <span>{template.default_venue}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-slate-400" />
                            <span>{template.default_start_time} - {template.default_end_time}</span>
                          </div>
                          {template.default_attendees?.length > 0 && (
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-slate-400" />
                              <span>{template.default_attendees.length} default attendees</span>
                            </div>
                          )}
                          {template.agenda_items?.length > 0 && (
                            <div className="flex items-center gap-2">
                              <ListOrdered className="w-4 h-4 text-slate-400" />
                              <span>{template.agenda_items.length} agenda items</span>
                            </div>
                          )}
                        </div>

                        <Button
                          onClick={() => openFromTemplate(template)}
                          className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 shadow-lg shadow-teal-500/25"
                          data-testid={`use-template-${template.id}`}
                        >
                          <Zap className="w-4 h-4 mr-2" />
                          Create Meeting
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {/* Create from Template Modal */}
      <Dialog open={showFromTemplateModal} onOpenChange={setShowFromTemplateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              Create Meeting from Template
            </DialogTitle>
          </DialogHeader>
          {selectedTemplate && (
            <form onSubmit={handleCreateFromTemplate} className="space-y-4 mt-4">
              <div className="p-4 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl border border-teal-100">
                <p className="text-sm font-semibold text-teal-700">
                  Using template: {selectedTemplate.name}
                </p>
                <p className="text-xs text-teal-600 mt-1">
                  {selectedTemplate.committee_name}
                  {selectedTemplate.default_attendees?.length > 0 &&
                    ` • ${selectedTemplate.default_attendees.length} attendees`}
                  {selectedTemplate.agenda_items?.length > 0 &&
                    ` • ${selectedTemplate.agenda_items.length} agenda items`}
                </p>
              </div>

              {selectedTemplate.agenda_items?.length > 0 && (
                <div className="border border-slate-200 rounded-xl p-4">
                  <p className="text-xs font-semibold text-slate-600 mb-3">Agenda Items:</p>
                  <div className="space-y-2">
                    {selectedTemplate.agenda_items.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <span className="w-5 h-5 flex items-center justify-center text-xs font-bold bg-teal-100 text-teal-700 rounded-full flex-shrink-0">
                          {idx + 1}
                        </span>
                        <span className="text-slate-700">{item.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-slate-700 font-medium">Meeting Date *</Label>
                <Input
                  type="date"
                  value={fromTemplateData.date}
                  onChange={(e) =>
                    setFromTemplateData({ ...fromTemplateData, date: e.target.value })
                  }
                  required
                  className="mt-1.5"
                  data-testid="from-template-date"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700 font-medium">Start Time</Label>
                  <Input
                    type="time"
                    value={fromTemplateData.start_time}
                    onChange={(e) =>
                      setFromTemplateData({
                        ...fromTemplateData,
                        start_time: e.target.value,
                      })
                    }
                    className="mt-1.5"
                    data-testid="from-template-start-time"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium">End Time</Label>
                  <Input
                    type="time"
                    value={fromTemplateData.end_time}
                    onChange={(e) =>
                      setFromTemplateData({
                        ...fromTemplateData,
                        end_time: e.target.value,
                      })
                    }
                    className="mt-1.5"
                    data-testid="from-template-end-time"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-700 font-medium">Venue (optional override)</Label>
                <Input
                  value={fromTemplateData.venue}
                  onChange={(e) =>
                    setFromTemplateData({ ...fromTemplateData, venue: e.target.value })
                  }
                  placeholder={selectedTemplate.default_venue || "Enter venue"}
                  className="mt-1.5"
                  data-testid="from-template-venue"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowFromTemplateModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700"
                  disabled={creating}
                  data-testid="submit-from-template-btn"
                >
                  {creating ? "Creating..." : "Create Meeting"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
